// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { ActionMenuItem } from '@/components/action-menu-item';
import { EducationalTooltip } from '@/components/educational-tooltip';
import { PageContent } from '@/components/page-content';
import { SupportErrorBoundary } from '@/components/support-error-boundary';
import { TableControlContainer } from '@/components/table-control-container';
import {
  AddRounded,
  ArrowDropDownRounded,
  InfoOutlined,
  MultipleStopOutlined,
  SettingsRounded,
} from '@/constants/hardfin-standard-icons';
import { SEARCH_TERM_MAXIMUM_CHARACTERS } from '@/constants/search-term';
import { SortDirection, invert, sortDirectionSchema } from '@/constants/sort';
import {
  ContactFilter,
  ContactSortProperty,
  contactSortPropertySchema,
} from '@/contact/api/get-contacts';
import { ContactDataImportDialog } from '@/contact/components/contact-data-import-dialog';
import { ContactTable } from '@/contact/components/contact-table';
import { CreateContactDialog } from '@/contact/components/create-contact-dialog';
import { CustomerFilterMenu } from '@/customer/components/customer-filter-menu';
import { useDisclosure } from '@/hooks/use-disclosure';
import { useLocalStorage } from '@/hooks/use-local-storage';
import { searchTermSchema } from '@/utilities/search-term';
import {
  Badge,
  Box,
  Button,
  ButtonGroup,
  Checkbox,
  FormControlLabel,
  IconButton,
  Menu,
  Popover,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { createFileRoute } from '@tanstack/react-router';
import { debounce, omit } from 'lodash-es';
import { ChangeEventHandler, Suspense, useMemo, useRef, useState } from 'react';
import { z } from 'zod';

const LOCAL_STORAGE_SORT_KEY = 'contact-sort';

const DEFAULT_SORT_PROPERTY: ContactSortProperty = 'name';
const DEFAULT_SORT_DIRECTION: SortDirection = 'asc';

const SHOW_ARCHIVED_TOOLTIP_TITLE =
  'Show archived contacts. Archived contacts are hidden by default in the contacts list.';

const sortSchema = z.object({
  property: contactSortPropertySchema,
  direction: sortDirectionSchema,
});

const searchSchema = z.object({
  query: searchTermSchema,
  archived: z.boolean().optional(),
  customers: z.array(z.string()).optional(),
  direction: sortDirectionSchema.optional(),
  sort: contactSortPropertySchema.optional(),
  page: z.number().optional(),
});

export const Route = createFileRoute('/_app/contacts/')({
  component: ContactsRoute,
  validateSearch: searchSchema,
});

function ContactsRoute() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();

  const createDialog = useDisclosure();
  const uploadDialog = useDisclosure();
  const actionMenu = useDisclosure();
  const actionGroupRef = useRef<HTMLDivElement>(null);
  const menu = useDisclosure();
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const infoTooltip = useDisclosure();
  const infoButtonRef = useRef<HTMLButtonElement>(null);
  const [searchValue, setSearchValue] = useState(search.query ?? '');

  const [sortPreference, setSortPreference] = useLocalStorage(
    LOCAL_STORAGE_SORT_KEY,
    sortSchema
  );

  const debouncedUpdate = useMemo(
    () =>
      debounce((value: string) => {
        navigate({
          search: { ...omit(search, 'page'), query: value || undefined },
        });
      }, 300),
    [navigate, search]
  );

  const customerIds = search.customers ?? [];

  const sort = {
    property: search.sort ?? sortPreference?.property ?? DEFAULT_SORT_PROPERTY,
    direction:
      search.direction ?? sortPreference?.direction ?? DEFAULT_SORT_DIRECTION,
  };

  const isBadgeInvisible = search.archived === undefined;

  const filter: ContactFilter = {
    search: search.query,
    customerIds: search.customers,
    isArchived: search.archived ? 'all' : undefined,
  };

  const setSort = (property: ContactSortProperty, direction: SortDirection) => {
    setSortPreference({ property, direction });
    navigate({
      search: { ...omit(search, 'page'), direction, sort: property },
    });
  };

  const handleChangeQuery: ChangeEventHandler<HTMLInputElement> = (event) => {
    const value = event.target.value;
    setSearchValue(value);
    debouncedUpdate(value);
  };

  const handleChangeArchived = (_: unknown, isChecked: boolean) => {
    if (isChecked) {
      return navigate({
        search: { ...omit(search, 'page'), archived: true },
      });
    }

    navigate({ search: { ...omit(search, 'page'), archived: undefined } });
  };

  const handleChangeCustomers = (ids: string[]) => {
    navigate({
      search: {
        ...omit(search, 'page'),
        customers: ids.length > 0 ? ids : undefined,
      },
    });
  };

  const handleSort = (property: ContactSortProperty) => {
    if (property === sort.property) {
      return setSort(property, invert(sort.direction));
    }

    setSort(property, 'asc');
  };

  const handlePageChange = (page: number) => {
    navigate({ search: { ...search, page } });
  };

  return (
    <>
      <ContactDataImportDialog
        isOpen={uploadDialog.isOpen}
        onClose={uploadDialog.close}
      />
      <CreateContactDialog
        isOpen={createDialog.isOpen}
        onClose={createDialog.close}
      />
      <PageContent>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="h6">Contacts</Typography>
            <IconButton
              ref={infoButtonRef}
              size="small"
              onClick={infoTooltip.open}
              aria-label="Learn about contacts"
            >
              <InfoOutlined fontSize="small" />
            </IconButton>
          </Stack>

          <EducationalTooltip
            title="Contacts"
            isOpen={infoTooltip.isOpen}
            onClose={infoTooltip.close}
            anchorElement={infoButtonRef.current}
            placement="bottom-start"
          >
            <Stack spacing={2}>
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Contacts and users
                </Typography>
                <Typography variant="body2">
                  <strong>Contacts</strong> are people at the companies you work
                  with, such as customers and suppliers. They are distinct from{' '}
                  <strong>users</strong>, who log in to Hardfin at your own
                  organization.
                </Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Using contacts
                </Typography>
                <Typography variant="body2">
                  Every contact belongs to one company, and can be selected on
                  records such as fulfillment orders to capture who to reach out
                  to. Archived contacts are hidden by default in the contacts
                  list.
                </Typography>
              </Box>
            </Stack>
          </EducationalTooltip>

          <ButtonGroup
            size="small"
            variant="contained"
            ref={actionGroupRef}
            sx={{ alignSelf: 'end' }}
          >
            <Button
              startIcon={<AddRounded fontSize="small" />}
              onClick={createDialog.open}
            >
              Add contact
            </Button>
            <Button onClick={actionMenu.open}>
              <ArrowDropDownRounded fontSize="small" />
            </Button>
          </ButtonGroup>
          <Menu
            open={actionMenu.isOpen}
            onClose={actionMenu.close}
            onClick={actionMenu.close}
            anchorEl={actionGroupRef.current}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            MenuListProps={{ disablePadding: true }}
            slotProps={{
              paper: {
                sx: { maxWidth: '100vw', width: '25rem', marginTop: 0.5 },
              },
            }}
          >
            <ActionMenuItem
              icon={<MultipleStopOutlined color="action" fontSize="small" />}
              label="Import contact data"
              description="Import contact data in bulk from a CSV file"
              onClick={uploadDialog.open}
            />
          </Menu>
        </Stack>

        <TableControlContainer>
          <Stack direction="row" marginRight="auto" spacing={1}>
            <CustomerFilterMenu
              value={customerIds}
              onChange={handleChangeCustomers}
            />
          </Stack>
          <TextField
            size="small"
            label="Search"
            value={searchValue}
            inputProps={{ maxLength: SEARCH_TERM_MAXIMUM_CHARACTERS }}
            onChange={handleChangeQuery}
            placeholder="Search by name, email, phone, title, comments, or company"
            sx={{ width: { xs: '100%', sm: '20rem' } }}
          />
          <IconButton ref={menuButtonRef} onClick={menu.open}>
            <Badge variant="dot" color="error" invisible={isBadgeInvisible}>
              <SettingsRounded fontSize="small" />
            </Badge>
          </IconButton>
          <Popover
            anchorEl={menuButtonRef.current}
            open={menu.isOpen}
            onClose={menu.close}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <Stack paddingY={0.5}>
              <Tooltip
                arrow
                placement="left"
                title={SHOW_ARCHIVED_TOOLTIP_TITLE}
              >
                <FormControlLabel
                  sx={{ paddingLeft: 2, paddingY: 0.5 }}
                  label={
                    <Typography variant="body2">
                      Include archived contacts
                    </Typography>
                  }
                  control={
                    <Checkbox
                      size="small"
                      checked={search.archived ?? false}
                      onChange={handleChangeArchived}
                    />
                  }
                />
              </Tooltip>
            </Stack>
          </Popover>
        </TableControlContainer>

        <SupportErrorBoundary message="An error occurred loading contacts">
          <Suspense>
            <ContactTable
              sort={sort}
              filter={filter}
              page={search.page ?? 1}
              pageSize={25}
              onSort={handleSort}
              onPageChange={handlePageChange}
            />
          </Suspense>
        </SupportErrorBoundary>
      </PageContent>
    </>
  );
}
